import { currencySymbol, cyclesOn, MONTHLY_DAYS, monthlyCycles } from '../round-config'
import { howOftenPicker } from '../how-often'
import { dismiss, getQueue, mutate, type Ack } from '../api'
import { openBooking } from '../booking'
import { glowAddJobOnce } from '../feature-guide'
import { everyWords, extraJobOf, jobOneOf, lastDoneOf, stopAsksAbout, suggestFirstDate, visitsOf, windowsDayBefore, type CustomerJob, type Job, type JobKind } from '../jobs'
import { isContact } from '../planning'
import { keepPendingJob, pendingJobFor } from '../pending-jobs'
import { cursorOnOpen, keepAboveKeyboard } from '../sheet-keyboard'
import { kindLabel } from '../trade'
import type { Clean, Customer } from '../types'
import { clear, dayLabel, el, money, parseMoney, shortDate, toast, todayISO } from '../ui'

// The customer page's Jobs (several jobs step 2, PLAN 3; Liam's answers 2, 6, 7, 8, 11, 12). Only on a round with
// several jobs switched on.
//
// - One job: the page is as it always was, with "Add another job" under the price.
// - More than one (or the windows stopped while gutters carry on): a Jobs box, one line per job,
//   "Windows · £20 · every 4 weeks · next Thu 9 Oct", each with Change, Book and Stop, then "Add another job".
// - Adding asks for the name, price, how often and the first day. Roundly suggests the day the windows are next
//   booked: gutters go on the windows day or before it, never after (Liam Q11). The owner picks.
// - Stopping asks about each visit already planned or agreed for that job, keep or remove (Liam Q7). Windows can
//   stop while gutters carry on (Liam Q8). Past visits and money stay where they are.
// - Changing the price never alters visits already booked; changing how often leaves them where they are and the
//   next one after uses the new gap (Liam Q12).

export interface JobsContext {
  customer: Customer
  cleans: Clean[]
  jobs: CustomerJob[]
  reload: () => void
}

// What the job is called on this page: its own name, else the trade's word for its kind (job 1: windows).
export function jobName(job: Job): string {
  return job.name?.trim() || kindLabel(job.kind ?? 'window')
}

function jobsOfPage(ctx: JobsContext): Job[] {
  return [jobOneOf(ctx.customer), ...ctx.jobs.filter((j) => !j.deleted_at).sort((a, b) => a.id - b.id).map(extraJobOf)]
}

// The job's next visit from today on (not a contact reminder), and whether anyone agreed it.
function nextOf(job: Job, cleans: Clean[], today: string): Clean | null {
  return visitsOf(job, cleans)
    .filter((v) => v.state === 'booked' && v.date >= today && !isContact(v))
    .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)[0] ?? null
}

export function jobLine(job: Job, cleans: Clean[], today: string): string {
  const bits = [jobName(job)]
  if (job.stopped_on) return `${bits[0]} · stopped ${shortDate(job.stopped_on)}`
  bits.push(job.price_pence !== null ? money(job.price_pence) : 'no price yet')
  bits.push(everyWords(job.cycle_days) ?? 'no repeat')
  const next = nextOf(job, cleans, today)
  // "Day agreed" in the shared words (visit-words.ts) when it is; the visit's own row below says the rest. "planned" is
  // a retired word (bug hunt 8 Oct 2026; docs/DIARY-AND-MESSAGES.md section 2).
  bits.push(next ? `next ${dayLabel(next.date)}${next.confirmed ? ', Day agreed' : ''}` : 'nothing booked')
  return bits.join(' · ')
}

// The customer has more than the one job: a second job, or their windows stopped while they stay.
export function showsJobsBox(ctx: Pick<JobsContext, 'customer' | 'jobs'>): boolean {
  return ctx.jobs.some((j) => !j.deleted_at) || !!ctx.customer.job_stopped_on
}

export function addJobButton(ctx: JobsContext, className = 'link-button add-job'): HTMLElement {
  const b = el('button', { type: 'button', class: className }, el('span', { class: 'add-job-plus', 'aria-hidden': 'true' }, '+'), ' Add another job')
  b.addEventListener('click', () => openAddJob(ctx))
  // Just switched on from Extra features: the first one drawn glows, once (feature-guide.ts).
  glowAddJobOnce(b)
  return b
}

export function jobsBox(ctx: JobsContext): HTMLElement {
  const today = todayISO()
  const box = el('section', { class: 'jobs-box', 'aria-label': 'Jobs' }, el('h3', {}, 'Jobs'))
  const list = el('div', { class: 'jobs-list' })
  for (const job of jobsOfPage(ctx)) {
    const row = el('div', { class: `job-row${job.stopped_on ? ' job-row-stopped' : ''}` })
    row.append(el('span', { class: 'job-line' }, jobLine(job, ctx.cleans, today)))
    const acts = el('span', { class: 'job-actions' })
    const change = el('button', { type: 'button' }, 'Change')
    change.addEventListener('click', () => openChangeJob(ctx, job))
    acts.append(change)
    if (!job.stopped_on) {
      const book = el('button', { type: 'button' }, 'Book')
      book.addEventListener('click', () => openBooking({ customerId: ctx.customer.id, jobId: job.id }))
      const stop = el('button', { type: 'button', class: 'btn-danger' }, 'Stop')
      stop.addEventListener('click', () => openStopJob(ctx, job))
      acts.append(book, stop)
    }
    row.append(acts)
    list.append(row)
  }
  box.append(list, addJobButton(ctx, 'btn-secondary add-job'))
  return box
}

// ---- the sheet the three steps open in -------------------------------------------------------------------------

function sheet(title: string): { dialog: HTMLDialogElement; content: HTMLElement; close: () => void } {
  const dialog = el('dialog', { class: 'booking-dialog jobs-dialog', 'aria-label': title }) as HTMLDialogElement
  const shut = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const content = el('div', { class: 'booking-content' })
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, title), shut), content)
  const close = (): void => dialog.close()
  shut.addEventListener('click', close)
  // Above the iPhone keyboard while a box is typed in, as the other sheets (sheet-keyboard.ts, 20 Oct 2026).
  const free = keepAboveKeyboard(dialog)
  dialog.addEventListener('close', () => { free(); dialog.remove() })
  document.body.append(dialog)
  dialog.showModal()
  return { dialog, content, close }
}

// A refusal the screen answers itself is not left in the saving indicator as "needs review".
function letGo(path: string): void {
  for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === path) dismiss(q.key)
}

// The interval a job has is kept exactly as it is (30 days stays 30 days; Astra r1, finding 5): it shows as itself,
// "Now: every 30 days", and is only changed when the owner changes this control (`touched`). Since 27 Oct 2026 the one
// How often question (src/how-often.ts, design D): Regular and its weeks, or Just once (no repeat). A job has no Ad hoc
// and no first clean (Astra's check, finding 10).
function howOftenControl(current: number | null): { node: HTMLElement; value: () => number | null | 'bad'; touched: () => boolean } {
  let touched = false
  const picker = howOftenPicker({ value: current ? { days: current, marker: null } : { days: null, marker: 'Just once' }, kinds: ['regular', 'once'], question: 'How often?', onChange: () => { touched = true } })
  return {
    node: el('div', { class: 'how-often' }, picker.node),
    touched: () => touched,
    value: () => {
      if (!touched) return current
      const v = picker.value()
      if (picker.kind() === 'once') return null
      return v?.days ?? 'bad'
    },
  }
}

const KIND_CHOICES: [JobKind, string][] = [['gutter', 'Gutters'], ['window', 'Windows'], ['other', 'Something else']]

// ---- Add another job --------------------------------------------------------------------------------------------

export function openAddJob(ctx: JobsContext, preset: { kind?: JobKind; date?: string } = {}): void {
  const c = ctx.customer
  const { content, close } = sheet('Add another job')
  const today = todayISO()
  content.append(el('h3', {}, c.name), el('p', { class: 'muted' }, `A job has its own price and how often, like Gutters every 26 weeks at ${currencySymbol()}60.`))
  const form = el('form', { class: 'booking-form' })
  const fields = el('fieldset', { class: 'booking-fields' })
  const kind = el('select', {}) as HTMLSelectElement
  for (const [v, label] of KIND_CHOICES) kind.append(el('option', { value: v }, v === 'other' ? label : kindLabel(v)))
  kind.value = preset.kind ?? 'gutter'
  const name = el('input', { maxlength: '100', placeholder: 'Gutters' }) as HTMLInputElement
  let named = false
  name.addEventListener('input', () => { named = true })
  const price = el('input', { inputmode: 'decimal', placeholder: 'Not priced yet' }) as HTMLInputElement
  const often = howOftenControl(kind.value === 'gutter' ? 182 : 28)
  const date = el('input', { type: 'date' }) as HTMLInputElement
  date.min = today
  const agreed = el('input', { type: 'checkbox' }) as HTMLInputElement
  // Roundly's suggestion for the first day: the windows' next booked day, never after it (Liam Q11).
  const windows = jobOneOf(c)
  const windowsNext = ctx.customer.job_stopped_on ? null : nextOf(windows, ctx.cleans, today)?.date ?? null
  const windowsLast = lastDoneOf(windows, ctx.cleans)
  const windowsDue = windowsLast && windows.cycle_days ? cyclesOn(windowsLast, windows.cycle_days) : null
  const suggestion = suggestFirstDate(windowsNext, windowsDue, today)
  date.value = preset.date ?? suggestion.date
  const hint = el('p', { class: 'muted field-help' })
  const explain = (): void => {
    const word = (name.value.trim() || kindLabel(kind.value as JobKind)).toLowerCase()
    if (date.value === suggestion.date && suggestion.why === 'windows') hint.textContent = `Roundly suggests ${dayLabel(suggestion.date)}, the day the windows are booked, so the ${word} are done first that day. Pick another day if you like, but not after the windows.`
    else if (date.value === suggestion.date && suggestion.why === 'due') hint.textContent = `Roundly suggests ${dayLabel(suggestion.date)}, when the windows are next due.`
    else if (kind.value === 'gutter' && windowsDayBefore(date.value, ctx.cleans, today)) hint.textContent = `That is after the windows on ${dayLabel(windowsDayBefore(date.value, ctx.cleans, today)!)}. Gutters go on the windows day or the day before, never after, because the gutters can drop dirt on clean windows.`
    else hint.textContent = date.value ? `First visit ${dayLabel(date.value)}.` : 'No first visit yet. Book it later from the Jobs box.'
  }
  const fillName = (): void => {
    if (!named) name.value = kind.value === 'other' ? '' : kindLabel(kind.value as JobKind)
    explain()
  }
  kind.addEventListener('change', fillName)
  date.addEventListener('input', explain)
  name.addEventListener('input', explain)
  fillName()
  const field = (label: string, node: HTMLElement): HTMLElement => el('label', { class: 'booking-field' }, el('span', {}, label), node)
  fields.append(
    field('What kind of job', kind),
    field('Name', name),
    field(`Price ${currencySymbol()}`, price),
    often.node,
    field('First visit', date),
    hint,
    el('label', { class: 'check-label' }, agreed, el('span', {}, 'The customer has agreed this day')),
  )
  const error = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Add job')
  const cancel = el('button', { type: 'button' }, 'Cancel')
  cancel.addEventListener('click', close)
  form.append(fields, el('footer', { class: 'booking-footer' }, error, cancel, save))
  content.append(form)
  cursorOnOpen(name)
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    error.textContent = ''
    const jobName = name.value.trim() || kindLabel(kind.value as JobKind)
    let pence: number | null
    try { pence = parseMoney(price.value) } catch (err) { error.textContent = (err as Error).message; return }
    const days = often.value()
    if (days === 'bad') { error.textContent = 'Pick every how many weeks, or Just once.'; return }
    if (date.value && date.value < today) { error.textContent = 'The first visit cannot be in the past.'; return }
    // Gutters never after the windows (Liam Q11): a day up to a fortnight after a booked windows visit is refused,
    // with the windows day one tap away.
    const windowsDay = kind.value === 'gutter' && date.value ? windowsDayBefore(date.value, ctx.cleans, today) : null
    if (windowsDay) {
      error.textContent = `The windows are booked ${dayLabel(windowsDay)}. Gutters go on the windows day or the day before, never after.`
      const use = el('button', { type: 'button', class: 'link-button' }, `Use ${dayLabel(windowsDay)}`)
      // A different day: the agreement ticked for the old one does not carry over (Astra r2, finding 4).
      use.addEventListener('click', () => { date.value = windowsDay; agreed.checked = false; error.textContent = ''; explain() })
      error.append(' ', use)
      return
    }
    // One new job at a time while the last is still waiting for signal (Astra r1, finding 11).
    const waiting = pendingJobFor(c.id)
    if (waiting) { error.textContent = `${waiting.jobName} is still waiting for signal. It saves, with its first visit, once your phone is back in signal.`; return }
    save.disabled = true
    cancel.disabled = true
    const sameDayFirst = date.value ? ctx.cleans.find((v) => v.state === 'booked' && v.date === date.value && !isContact(v)) : undefined
    const firstVisit = date.value
      ? { date: date.value, kind: kind.value, ...(pence !== null ? { amount_pence: pence } : {}), state: 'booked', confirmed: agreed.checked ? 1 : 0, allow_additional: false,
          ...(sameDayFirst?.worker ? { worker: sameDayFirst.worker } : {}), ...(sameDayFirst?.time_slot ? { time_slot: sameDayFirst.time_slot } : {}) }
      : null
    let queuedKey: string | null = null
    const made = await mutate<{ id: number; version: number }>({
      label: `Add job: ${jobName} for ${c.name}`,
      method: 'POST',
      path: `/api/customers/${c.id}/jobs`,
      body: { name: jobName, kind: kind.value, price_pence: pence, cycle_days: days, cycle_text: null },
      onQueued: (m) => { queuedKey = m.key },
      jobsAware: true,
    })
    if (!made.ok) {
      save.disabled = false
      cancel.disabled = false
      if (queuedKey) {
        // Held in the queue for any reason (no signal, a server error, an expired sign-in: Astra r2, finding 10):
        // the job waits there and its first visit waits with it, booked the moment it saves.
        keepPendingJob({ jobKey: queuedKey, customerId: c.id, jobName, visit: firstVisit })
        close()
        toast(`${jobName}${firstVisit ? ' and its first visit' : ''} aren't saved yet. They save as soon as they can.`)
        return
      }
      letGo(`/api/customers/${c.id}/jobs`)
      error.textContent = made.error ?? "That didn't save."
      return
    }
    const jobId = made.row?.id
    if (!date.value || typeof jobId !== 'number') {
      close()
      toast(`${jobName} added`)
      ctx.reload()
      return
    }
    // On a day the customer already has a visit booked (the windows day), the new job joins that stop: the same
    // person and time, so it is one stop on their Today (Liam Q9).
    const sameDay = ctx.cleans.find((v) => v.state === 'booked' && v.date === date.value && !isContact(v))
    const booked = await mutate({
      label: `Book: ${c.name} ${jobName} ${date.value}`,
      method: 'POST',
      path: `/api/customers/${c.id}/cleans`,
      body: {
        date: date.value, job_id: jobId, kind: kind.value, ...(pence !== null ? { amount_pence: pence } : {}), state: 'booked', confirmed: agreed.checked ? 1 : 0, allow_additional: false,
        ...(sameDay?.worker ? { worker: sameDay.worker } : {}), ...(sameDay?.time_slot ? { time_slot: sameDay.time_slot } : {}),
      },
    })
    close()
    if (booked.ok) toast(`${jobName} added, first visit ${dayLabel(date.value)}`)
    else if (booked.status === 0) toast(`${jobName} added. The first visit saves when your phone is back in signal.`)
    else toast(`${jobName} added, but the first visit didn't save: ${booked.error ?? 'refused'}. Book it from the Jobs box.`)
    ctx.reload()
  })
}

// ---- Change -----------------------------------------------------------------------------------------------------

export function openChangeJob(ctx: JobsContext, job: Job): void {
  const c = ctx.customer
  const extra = job.id === null ? null : ctx.jobs.find((j) => j.id === job.id) ?? null
  const { content, close } = sheet(`Change ${jobName(job)}`)
  content.append(el('h3', {}, c.name), el('p', { class: 'muted' }, 'A new price is for visits booked from now on. Visits already booked keep their day; the one after uses the new gap.'))
  const form = el('form', { class: 'booking-form' })
  const fields = el('fieldset', { class: 'booking-fields' })
  const field = (label: string, node: HTMLElement): HTMLElement => el('label', { class: 'booking-field' }, el('span', {}, label), node)
  const name = el('input', { maxlength: '100' }) as HTMLInputElement
  name.value = job.id === null ? (c.job_name ?? '') : (job.name ?? '')
  name.placeholder = kindLabel(job.kind ?? 'window')
  const price = el('input', { inputmode: 'decimal', placeholder: 'Not priced yet' }) as HTMLInputElement
  price.value = job.price_pence === null ? '' : (job.price_pence / 100).toFixed(2)
  const often = howOftenControl(job.cycle_days)
  fields.append(field('Name', name), field(`Price ${currencySymbol()}`, price), often.node)
  const error = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save')
  const cancel = el('button', { type: 'button' }, 'Cancel')
  cancel.addEventListener('click', close)
  form.append(fields, el('footer', { class: 'booking-footer' }, error, cancel, save))
  content.append(form)
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    error.textContent = ''
    let pence: number | null
    try { pence = parseMoney(price.value) } catch (err) { error.textContent = (err as Error).message; return }
    const days = often.value()
    if (days === 'bad') { error.textContent = 'Pick every how many weeks, or Just once.'; return }
    const newName = name.value.trim()
    if (job.id !== null && !newName) { error.textContent = 'Give the job a name.'; return }
    const body: Record<string, unknown> = {}
    let path: string
    if (job.id === null) {
      path = `/api/customers/${c.id}`
      body.version = c.version
      if ((newName || null) !== (c.job_name ?? null)) body.job_name = newName || null
      if (pence !== c.reg_price_pence) body.reg_price_pence = pence
      // How often only changes when the owner changed it.
      if (often.touched() && days !== (c.cycle_days ?? null)) {
        body.cycle_days = days
        body.cycle_text = null
      }
    } else {
      path = `/api/customer-jobs/${job.id}`
      body.version = extra?.version
      if (newName !== job.name) body.name = newName
      if (pence !== job.price_pence) body.price_pence = pence
      if (often.touched() && days !== job.cycle_days) {
        body.cycle_days = days
        body.cycle_text = null
      }
    }
    if (Object.keys(body).length === 1) { close(); return }
    save.disabled = true
    const ack: Ack = await mutate({ label: `Change ${jobName(job)}: ${c.name}`, method: 'PATCH', path, body })
    if (ack.ok || ack.status === 0) {
      close()
      toast(ack.ok ? `${newName || jobName(job)} changed` : 'Saved on this phone. It sends when you have signal.')
      ctx.reload()
      return
    }
    save.disabled = false
    letGo(path)
    error.textContent = ack.status === 409 ? 'This was changed on another phone. Close this and look again.' : ack.error ?? "That didn't save."
  })
}

// ---- Stop -------------------------------------------------------------------------------------------------------

export function openStopJob(ctx: JobsContext, job: Job): void {
  const c = ctx.customer
  const word = jobName(job)
  const today = todayISO()
  const asked = stopAsksAbout(job, ctx.cleans, today)
  const { content, close } = sheet(`Stop ${word}`)
  content.append(el('h3', {}, c.name))
  const others = jobsOfPage(ctx).filter((j) => j.id !== job.id && !j.stopped_on)
  content.append(el('p', {}, others.length
    ? `Roundly stops booking ${word.toLowerCase()} for ${c.name}. ${others.length === 1 ? `The other job, ${jobName(others[0]!)}, carries` : `The other jobs, ${others.map(jobName).join(' and ')}, carry`} on as now.`
    : `Roundly stops booking ${word.toLowerCase()} for ${c.name}. They stay a customer.`))
  content.append(el('p', { class: 'muted' }, 'Past visits and money stay on their page. Dates Roundly planned by itself for this job are taken off.'))
  const form = el('form', { class: 'booking-form' })
  if (asked.length) {
    const box = el('fieldset', { class: 'stop-visits' }, el('legend', {}, asked.length === 1 ? 'This visit is already booked. Keep it or take it off?' : 'These visits are already booked. Keep each one or take it off?'))
    for (const v of asked) {
      const keep = el('input', { type: 'radio', name: `stop-${v.id}`, value: 'keep' }) as HTMLInputElement
      const remove = el('input', { type: 'radio', name: `stop-${v.id}`, value: 'remove' }) as HTMLInputElement
      box.append(el('div', { class: 'stop-visit' },
        el('span', {}, `${dayLabel(v.date)} · ${money(v.amount_pence)}${v.confirmed ? ' · Day agreed' : ''}`),
        el('label', { class: 'check-label' }, keep, el('span', {}, 'Keep')),
        el('label', { class: 'check-label' }, remove, el('span', {}, 'Take off'))))
    }
    form.append(box)
  }
  const error = el('p', { class: 'form-error', role: 'alert' })
  const save = el('button', { type: 'submit', class: 'btn-danger' }, `Stop ${word.toLowerCase()}`)
  const cancel = el('button', { type: 'button' }, 'Cancel')
  cancel.addEventListener('click', close)
  form.append(el('footer', { class: 'booking-footer' }, error, cancel, save))
  content.append(form)
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    error.textContent = ''
    const visits: { id: number; version: number; keep: boolean }[] = []
    for (const v of asked) {
      const picked = form.querySelector<HTMLInputElement>(`input[name="stop-${v.id}"]:checked`)
      if (!picked) { error.textContent = `Choose Keep or Take off for ${dayLabel(v.date)}.`; return }
      visits.push({ id: v.id, version: v.version, keep: picked.value === 'keep' })
    }
    const version = job.id === null ? c.version : ctx.jobs.find((j) => j.id === job.id)?.version
    save.disabled = true
    cancel.disabled = true
    const path = `/api/customers/${c.id}/jobs/stop`
    const ack = await mutate<{ removed?: number[] }>({ label: `Stop ${word}: ${c.name}`, method: 'POST', path, body: { job_id: job.id, version, visits } })
    if (ack.ok || ack.status === 0) {
      close()
      toast(ack.ok ? `${word} stopped for ${c.name}` : `Saved on this phone. ${word} stops when you have signal.`)
      ctx.reload()
      return
    }
    letGo(path)
    if (ack.code === 'visits_changed') {
      // Nothing was stopped: a visit was done, moved or added on another phone. Asked again with them as they are.
      close()
      toast(`The booked visits for ${word.toLowerCase()} changed on another phone. Tap Stop again to check them.`)
      ctx.reload()
      return
    }
    save.disabled = false
    cancel.disabled = false
    error.textContent = ack.status === 409 ? 'This job was changed on another phone. Close this and look again.' : ack.error ?? "That didn't save."
  })
}
